import { FLAGS } from '@iheartjane/feature-flags';
import type { CompareProduct } from '@iheartjane/menu';
import {
  CompareProducts,
  MAX_COMPARE,
  ProductCardText,
  hideStrikethroughPrice,
  useCompare,
} from '@iheartjane/menu';
import {
  Button,
  Card,
  CheckIcon,
  DismissIcon,
  JaneGoldIcon,
  Modal,
  Popover,
  Tag,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import compact from 'lodash-es/compact';
import type { SyntheticEvent } from 'react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Form, useNavigate, useSubmit } from 'react-router';

import { BagTransferModal } from '@jane/bloom/components/bagTransferModal/bagTransferModal';
import { BloomLink } from '@jane/bloom/components/bloomLink';
import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { ProductCategoryLabel } from '@jane/bloom/components/productCategoryLabel/productCategoryLabel';
import { useMarketplaceCart } from '@jane/bloom/hooks/cart/useMarketplaceCart';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useAddToCartConfig } from '@jane/bloom/hooks/useAddToCartConfig';
import { useOptimisticBagSize } from '@jane/bloom/hooks/useBagSize';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import {
  trackAddedProductToCompare,
  trackClickedCompareModalProduct,
  trackProductListingTap,
} from '@jane/bloom/services/tracking/track';
import { getGoldLabel } from '@jane/bloom/utils/badges/getGoldLabel';
import { mergeCartItem } from '@jane/bloom/utils/cart/mergeCartItem';
import { getMaxCartQuantity } from '@jane/bloom/utils/getMaxCartQuantity';
import { formatWeight } from '@jane/bloom/utils/strings/formatWeight';
import { buildCompareProduct } from '@jane/bloom/viewModels/compareProduct';
import { hasSpecialApplied as hasSpecial } from '@jane/bloom/viewModels/getPrice';

import { AddToCartLabel } from './components/addToCartLabel';
import { SelectWeightForm } from './components/selectWeightForm';
import { SpecialBadge } from './components/specialBadge';
import type { ProductCardProps } from './productCard';

import styles from './productCard.module.css';

export const LegacyProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  function LegacyProductCard(
    {
      brandSpecials,
      customRowId,
      isPlacement,
      isSponsored,
      maxWeightOption,
      preloaded,
      product,
      selected,
      smartProductInstance,
      weightFilter,
    },
    ref
  ) {
    const { fulfillmentMode, pinnedStore } = useStoreSwitcher();

    const formattedWeightKeys = Object.keys(product.availableWeightsFormatted);
    const selectedWeight =
      formattedWeightKeys[0] ?? product.available_weights?.[0] ?? 'each';

    const hasSpecialApplied = hasSpecial(product);

    const price =
      product.availableWeightsFormatted[selectedWeight ?? 'each']
        ?.priceFormatted;
    const priceBeforeDiscounts =
      product.availableWeightsFormatted[selectedWeight ?? 'each']
        ?.fullPriceFormatted;
    const priceIsDiscounted =
      priceBeforeDiscounts && price !== priceBeforeDiscounts;

    const { cart, featureFlags, shoppingPreferences } = useRootLoaderData();
    const submit = useSubmit();
    const navigate = useNavigate();
    const {
      fetcher: { state },
      formArgs,
    } = useAddToCartConfig({
      product,
      productId: product.product_id,
      quantity: 1,
      rowId: customRowId,
      selectedWeight,
    });
    const { createCart, deleteCart, newCart } = useMarketplaceCart();

    const isAddingToCart = state === 'submitting';
    const countInCart = useOptimisticBagSize(product.product_id);
    const atMaxQuantity =
      getMaxCartQuantity({
        cart,
        currentProduct: product,
        currentWeight: selectedWeight,
        storeId: shoppingPreferences?.storeId,
      }) === 0;
    const [isWeightFormOpen, setIsWeightFormOpen] = useState(false);
    const [isCompareWeightOpen, setIsCompareWeightOpen] = useState(false);
    const [showWarningModal, setShowWarningModal] = useState(false);

    const onSdkProductClick = useCallback(
      (addToCart = false) => {
        smartProductInstance?.click({ isAddToCart: addToCart });
      },
      [smartProductInstance]
    );

    const onSubmit = (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
      onSdkProductClick(true);

      // if cart items in the cart are from a different from the current store, prevent the add to cart action and show warning modal
      const hasPreviousStoreProducts = cart.storeId
        ? cart.storeId !== shoppingPreferences?.storeId
        : false;

      if (hasPreviousStoreProducts) {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        const count = formData.get('count');
        const priceId = formData.get('priceId');
        const productId = formData.get('productId');

        if (shoppingPreferences) {
          createCart({
            products: mergeCartItem(
              {
                count: Number(count),
                price_id: priceId ? formatWeight(String(priceId)) : 'each',
                product_id: Number(productId),
              },
              cart.items
            ),
            reservationMode: shoppingPreferences.reservationMode,
            storeId: shoppingPreferences.storeId,
          });
        }

        setShowWarningModal(true);
      }
    };

    const confirmStartNewBag = () => {
      // close the modal
      setShowWarningModal(false);

      // replace the current cart with the new cart
      if (newCart) {
        submit({ shouldReplaceCart: 'true' }, formArgs);
      }
    };

    const handleCartWarningClose = () => {
      // close the modal
      setShowWarningModal(false);

      //delete the pending cart
      if (newCart) {
        deleteCart({ cartUuid: newCart?.cart.uuid });
      }
    };

    const janeGoldLabel = product.applicable_brand_special_ids
      ? getGoldLabel({
          applicableBrandSpecialIds: product.applicable_brand_special_ids,
          brandSpecials,
          lowestAvailablePriceVal:
            product.lowest_available_price &&
            'val' in product.lowest_available_price
              ? product.lowest_available_price.val
              : undefined,
        })
      : null;

    const { ref: impressionRef } = useInView({
      onChange: (inView) => {
        if (inView) {
          smartProductInstance?.impress();
        }
      },
      threshold: 0.5,
    });

    const compareEnabled = featureFlags[FLAGS.c1CompareProducts];
    const {
      isActive: isCompareActive,
      isSelected,
      productCount: compareCount,
      toggleProduct,
    } = useCompare();
    const toast = useToast();

    const compareProduct = useMemo<CompareProduct | null>(() => {
      if (!compareEnabled) return null;
      return buildCompareProduct({
        product,
        storeState: pinnedStore?.state,
      });
    }, [compareEnabled, product, pinnedStore?.state]);

    const handleCompareToggle = useCallback(() => {
      if (!compareProduct) return;

      const compareTrackingProps = {
        productBrand: product.brand,
        productId: product.product_id,
        productKind: product.kind,
        productName: product.name,
      };

      // The "View" link and product row both navigate to the PDP (via `url`),
      // so they map to a modal-product click — no add-to-cart there.
      const trackModalProductClick = () => {
        onSdkProductClick(); // DM (smart-sort) click
        trackClickedCompareModalProduct(compareTrackingProps);
      };

      // Pragmatic add from the compare modal: single-weight products with no
      // cross-store conflict add in one click. Multi-weight products open a
      // weight-selection modal stacked on top of the compare modal. Cross-store
      // carts need the BagTransferModal flow, which lives on the PDP, so they
      // fall back to opening the product page.
      const addFromCompareModal = () => {
        const hasPreviousStoreProducts = cart.storeId
          ? cart.storeId !== shoppingPreferences?.storeId
          : false;

        if (hasPreviousStoreProducts) {
          trackModalProductClick();
          if (compareProduct.url) navigate(compareProduct.url);
          return;
        }

        if (product.hasMultipleWeights) {
          onSdkProductClick(false); // DM click, no add-to-cart yet
          setIsCompareWeightOpen(true);
          return;
        }

        onSdkProductClick(true); // DM add-to-cart click
        submit(
          { count: 1, priceId: selectedWeight, productId: product.id },
          formArgs
        );
        toast.add({ label: 'Added to bag', time: 2500 });
      };

      const isAdding = !isSelected(product.product_id);
      const success = toggleProduct(compareProduct, {
        addToCart: addFromCompareModal,
        view: trackModalProductClick,
      });
      if (!success) {
        toast.add({
          label: `Maximum ${MAX_COMPARE} products for comparison`,
          time: 2500,
        });
      } else if (isAdding) {
        trackAddedProductToCompare(compareCount + 1, compareTrackingProps);
      }
    }, [
      compareProduct,
      toggleProduct,
      toast,
      product,
      isSelected,
      compareCount,
      onSdkProductClick,
      cart.storeId,
      shoppingPreferences?.storeId,
      navigate,
      submit,
      selectedWeight,
      formArgs,
    ]);

    // Tapping the product fires both DM (smart-sort) click tracking via
    // `selected` and the Mixpanel listing-tap event. Shared so the image and
    // the product-name links stay in sync.
    const handleProductTap = useCallback(() => {
      selected?.();
      trackProductListingTap(
        {
          fulfillmentMode,
          product,
          reservationMode: shoppingPreferences?.reservationMode,
          storeId: pinnedStore?.id ?? shoppingPreferences?.storeId,
        },
        featureFlags
      );
    }, [
      selected,
      fulfillmentMode,
      product,
      shoppingPreferences?.reservationMode,
      shoppingPreferences?.storeId,
      pinnedStore?.id,
      featureFlags,
    ]);

    const pdpPath = `/products/${smartProductInstance?.attributes.product_id ?? product.product_id}/${product.unique_slug ?? product.id}`;

    return (
      <div
        ref={ref}
        className={clsx(styles['productCard'], {
          [styles['productCardPlacement']]: isPlacement,
        })}
        data-testid="product-card"
      >
        <BagTransferModal
          open={showWarningModal}
          onConfirm={confirmStartNewBag}
          onRequestClose={handleCartWarningClose}
          newBag={newCart}
        />

        {/* Weight selection for multi-weight products added from the compare
            modal — opens on top of it so shoppers never leave the comparison. */}
        {compareProduct && (
          <ClientOnly>
            {() => (
              <Modal
                open={isCompareWeightOpen}
                onRequestClose={() => setIsCompareWeightOpen(false)}
                overlayClose
                variant="dialogue"
                contentLabel="Add to bag"
                overlayClassName={styles['compareWeightOverlay']}
                isLayered
              >
                <Modal.Header
                  title="Add to bag"
                  textAlign="center"
                  branded={false}
                />
                <Modal.Content>
                  <SelectWeightForm
                    maxWeightOption={maxWeightOption}
                    product={product}
                    weightFilter={weightFilter}
                    onSubmit={onSubmit}
                    onSuccess={() => {
                      setIsCompareWeightOpen(false);
                      toast.add({ label: 'Added to bag', time: 2500 });
                    }}
                    smartProductInstance={smartProductInstance}
                  />
                </Modal.Content>
              </Modal>
            )}
          </ClientOnly>
        )}

        <div className={styles['productCardContainer']}>
          <BloomLink
            ref={impressionRef}
            to={pdpPath}
            className="focus-visible"
            onClick={handleProductTap}
          >
            <Card
              height="100%"
              width="100%"
              flat
              className={styles['productCardImage']}
            >
              <div className={styles['badges']}>
                {product.shouldShowGold && janeGoldLabel && (
                  <Tag
                    background="gold"
                    color="grays-black"
                    icon={<JaneGoldIcon size="sm" />}
                    label={janeGoldLabel}
                    className={styles['janeGoldBadge']}
                  />
                )}
                {product.specialLabel && (
                  <SpecialBadge label={product.specialLabel} />
                )}
              </div>
              <Card.Image
                alt={product.name}
                src={product.image_urls?.[0]}
                srcSet={product.imageSrcSet?.srcSet}
                data-testid={product.unique_slug ?? ''}
                loading={preloaded ? 'eager' : 'lazy'}
              />
            </Card>
          </BloomLink>
          {isCompareActive && compareProduct && (
            <CompareProducts.Toggle
              isSelected={isSelected(product.product_id)}
              onToggle={handleCompareToggle}
              productName={product.name}
            />
          )}
        </div>

        <BloomLink
          to={pdpPath}
          style={{ textDecoration: 'none' }}
          onClick={handleProductTap}
        >
          <ProductCardText
            brandInfo={compact([product.brand, product.brand_subtype]).join(
              ' '
            )}
            dosageInfo={product.dosageAmountPackInfo}
            hasMultipleWeightDiscount={
              Boolean(hasSpecialApplied) &&
              Boolean(priceIsDiscounted) &&
              product.hasMultipleWeights
            }
            isSponsored={isSponsored}
            name={product.name}
            potencyInfo={
              product.basePotencyLabResults ?? product.potencyLabResults
            }
            ratingSummary={
              pinnedStore?.product_reviews_enabled
                ? product.aggregateRatingAndCount
                : null
            }
            renderProductCategoryLabel={({ className }) => (
              <ProductCategoryLabel
                category={product.category}
                className={className}
                kind={product.kind}
              />
            )}
            px={isPlacement ? 8 : 0}
          />
        </BloomLink>
        <div className={styles['priceAndAddToCartContainer']}>
          <div
            className={clsx(styles['productCardPrice'], {
              [styles['productCardPriceMultipleWeights']]:
                product.hasMultipleWeights,
            })}
          >
            {price && (
              <Typography
                variant="body-semibold"
                data-testid="product-price"
                color="text-main"
                className={clsx({
                  [styles['productCardDealsText']]: hasSpecialApplied,
                })}
              >
                {product.discountedPriceLabel}
              </Typography>
            )}
            {hasSpecialApplied &&
              priceBeforeDiscounts &&
              priceIsDiscounted &&
              !hideStrikethroughPrice(pinnedStore?.state || '') && (
                <Typography
                  color="grays-mid"
                  variant="small-body-regular"
                  strikeThrough
                >
                  {product.priceLabel}
                </Typography>
              )}
          </div>
          {product.hasMultipleWeights ? (
            <ClientOnly>
              {(isMobile) =>
                isMobile ? (
                  <Popover
                    mt={4}
                    targetWidth="100%"
                    label="Select weight"
                    closeOnTargetClick={false}
                    target={
                      <Button
                        variant="primary"
                        full
                        mt="auto"
                        disabled={isAddingToCart || atMaxQuantity}
                        onClick={() => onSdkProductClick(false)}
                        startIcon={
                          isAddingToCart && (
                            <CheckIcon
                              className={styles['textContrastColor']}
                            />
                          )
                        }
                        label={
                          <AddToCartLabel
                            atMaxQuantity={atMaxQuantity}
                            countInCart={countInCart}
                            hasMultipleWeights
                            isBeingAddedToCart={isAddingToCart}
                          />
                        }
                        className={styles['addToCartButton']}
                      />
                    }
                  >
                    {({ closePopover }) => (
                      <Popover.Content label="Select weight">
                        <div className={styles['productCardPopoverHeader']}>
                          <Button.Icon
                            icon={<DismissIcon />}
                            label="close"
                            onClick={closePopover}
                          />
                          <Typography
                            variant="h2-semibold"
                            textAlign="center"
                            color="text-main"
                          >
                            Add to bag
                          </Typography>
                        </div>
                        <SelectWeightForm
                          maxWeightOption={maxWeightOption}
                          product={product}
                          weightFilter={weightFilter}
                          onSubmit={onSubmit}
                          onSuccess={closePopover}
                          smartProductInstance={smartProductInstance}
                        />
                      </Popover.Content>
                    )}
                  </Popover>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      full
                      mt={4}
                      disabled={isAddingToCart || atMaxQuantity}
                      startIcon={
                        isAddingToCart && (
                          <CheckIcon className={styles['textContrastColor']} />
                        )
                      }
                      label={
                        <AddToCartLabel
                          atMaxQuantity={atMaxQuantity}
                          countInCart={countInCart}
                          hasMultipleWeights
                          isBeingAddedToCart={isAddingToCart}
                        />
                      }
                      className={styles['addToCartButton']}
                      onClick={() => {
                        onSdkProductClick(false);
                        setIsWeightFormOpen(true);
                      }}
                    />

                    <Modal
                      open={isWeightFormOpen}
                      onRequestClose={() => setIsWeightFormOpen(false)}
                      overlayClose
                      variant="dialogue"
                    >
                      <Modal.Header
                        title="Add to bag"
                        textAlign="center"
                        branded={false}
                      />
                      <Modal.Content>
                        <SelectWeightForm
                          maxWeightOption={maxWeightOption}
                          product={product}
                          weightFilter={weightFilter}
                          onSubmit={onSubmit}
                          onSuccess={() => setIsWeightFormOpen(false)}
                          smartProductInstance={smartProductInstance}
                        />
                      </Modal.Content>
                    </Modal>
                  </>
                )
              }
            </ClientOnly>
          ) : (
            <Form
              name={`${product.id}-add-to-cart`}
              className={styles['productCardAddToCartForm']}
              onSubmit={onSubmit}
              {...formArgs}
            >
              <input type="hidden" name="productId" value={product.id} />
              <input type="hidden" name="priceId" value={selectedWeight} />
              <input type="hidden" name="count" value={1} />

              <Button
                variant="primary"
                full
                disabled={isAddingToCart || atMaxQuantity}
                startIcon={
                  isAddingToCart && (
                    <CheckIcon className={styles['textContrastColor']} />
                  )
                }
                label={
                  <AddToCartLabel
                    atMaxQuantity={atMaxQuantity}
                    countInCart={countInCart}
                    hasMultipleWeights={false}
                    isBeingAddedToCart={isAddingToCart}
                  />
                }
                className={styles['addToCartButton']}
                data-testid="add-to-cart"
                type="submit"
              />
            </Form>
          )}
        </div>
      </div>
    );
  }
);
